'use client';
// Add + Reduce edit bars for HoldingsList — shown when the table is in
// edit mode. Extracted from HoldingsList.tsx to keep it under the 500-line
// god-file threshold. Pure dumb component — all state + handlers live in
// the parent.

import type { ScoreRow } from '@/lib/hooks/use-holdings-data';

interface PortfolioShape {
  entryPrices?: Record<string, number>;
  bookNav?: number;
}

interface Props {
  filterMode: 'all' | 'sell';
  saving: boolean;
  scores: Map<string, ScoreRow> | null;
  portfolio: PortfolioShape | null;
  // Add bar state
  addSym: string; setAddSym: (s: string) => void;
  addPrice: string; setAddPrice: (s: string) => void;
  addShares: string; setAddShares: (s: string) => void;
  addError: string | null; setAddError: (s: string | null) => void;
  commitError: string | null;
  handleAddPosition: () => void;
  // Reduce bar state
  reduceSym: string; setReduceSym: (s: string) => void;
  reduceShares: string; setReduceShares: (s: string) => void;
  reducePrice: string; setReducePrice: (s: string) => void;
  reduceError: string | null; setReduceError: (s: string | null) => void;
  handleReducePosition: () => void;
}

export function HoldingsEditBars({
  filterMode, saving, scores, portfolio,
  addSym, setAddSym, addPrice, setAddPrice, addShares, setAddShares,
  addError, setAddError, commitError, handleAddPosition,
  reduceSym, setReduceSym, reduceShares, setReduceShares, reducePrice, setReducePrice,
  reduceError, setReduceError, handleReducePosition,
}: Props) {
  if (filterMode === 'sell') return null;
  return (
    <>
      {/* Add bar */}
      <div className="px-2 py-1.5 flex items-center gap-2 text-[10px] border-b border-amber-500/20 bg-amber-500/5">
        <span className="uppercase tracking-wider text-[9px] text-emerald-400/80 font-semibold w-[60px]">+ Add</span>
        <input
          type="text"
          value={addSym}
          onChange={(e) => {
            const sym = e.target.value.toUpperCase();
            setAddSym(sym);
            setAddError(null);
            // Auto-fill the buy price with today's scoring price as a starting point.
            // Steve can override if back-dating a purchase at a different price.
            if (sym && !addPrice && scores?.has(sym)) {
              const px = scores.get(sym)?.price;
              if (px != null) setAddPrice(px.toFixed(2));
            }
          }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAddPosition(); }}
          placeholder="ticker"
          className="bg-[#0a0e14] border border-[#334155] focus:border-emerald-500/60 focus:outline-none rounded px-2 py-0.5 text-[11px] font-mono uppercase text-white w-[100px]"
          maxLength={8}
        />
        <input
          type="number"
          step="0.01"
          min="0"
          value={addPrice}
          onChange={(e) => { setAddPrice(e.target.value); setAddError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAddPosition(); }}
          placeholder="buy price $"
          className="bg-[#0a0e14] border border-[#334155] focus:border-emerald-500/60 focus:outline-none rounded px-2 py-0.5 text-[11px] tabular-nums text-emerald-200 w-[88px]"
          title="Cost basis — the price you paid per share. Persisted for tax-lot tracking."
        />
        <input
          type="number"
          step="1"
          min="0"
          value={addShares}
          onChange={(e) => { setAddShares(e.target.value); setAddError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAddPosition(); }}
          placeholder="shares"
          className="bg-[#0a0e14] border border-[#334155] focus:border-emerald-500/60 focus:outline-none rounded px-2 py-0.5 text-[11px] tabular-nums text-emerald-200 w-[80px]"
        />
        <button
          type="button"
          onClick={handleAddPosition}
          disabled={saving}
          className="px-2 py-0.5 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/15 hover:border-emerald-500/70 uppercase tracking-wide font-semibold text-[10px] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? 'saving…' : 'add'}
        </button>
        {addError && <span className="text-red-400 text-[10px]">{addError}</span>}
        {commitError && !addError && <span className="text-red-400 text-[10px]">{commitError}</span>}
        <span className="ml-auto text-[#64748b] text-[9px] italic">
          {addShares && addPrice && Number.isFinite(parseFloat(addShares) * parseFloat(addPrice))
            ? `≈ $${(parseFloat(addShares) * parseFloat(addPrice)).toLocaleString(undefined, { maximumFractionDigits: 0 })} · ${((parseFloat(addShares) * parseFloat(addPrice)) / (portfolio?.bookNav ?? 1_000_000) * 100).toFixed(2)}% of book`
            : 'saves + reloads; flows to every panel'}
        </span>
      </div>
      {/* Reduce bar */}
      <div className="px-2 py-1.5 flex items-center gap-2 text-[10px] border-b border-amber-500/20 bg-amber-500/5">
        <span className="uppercase tracking-wider text-[9px] text-amber-400/80 font-semibold w-[60px]">− Reduce</span>
        <input
          type="text"
          value={reduceSym}
          onChange={(e) => {
            const sym = e.target.value.toUpperCase();
            setReduceSym(sym);
            setReduceError(null);
            // Auto-fill sell price with today's scoring price.
            if (sym && !reducePrice && scores?.has(sym)) {
              const px = scores.get(sym)?.price;
              if (px != null) setReducePrice(px.toFixed(2));
            }
          }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleReducePosition(); }}
          placeholder="ticker"
          className="bg-[#0a0e14] border border-[#334155] focus:border-amber-500/60 focus:outline-none rounded px-2 py-0.5 text-[11px] font-mono uppercase text-white w-[100px]"
          maxLength={8}
        />
        <input
          type="number"
          step="1"
          min="0"
          value={reduceShares}
          onChange={(e) => { setReduceShares(e.target.value); setReduceError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleReducePosition(); }}
          placeholder="shares to sell"
          className="bg-[#0a0e14] border border-[#334155] focus:border-amber-500/60 focus:outline-none rounded px-2 py-0.5 text-[11px] tabular-nums text-amber-200 w-[100px]"
        />
        <input
          type="number"
          step="0.01"
          min="0"
          value={reducePrice}
          onChange={(e) => { setReducePrice(e.target.value); setReduceError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleReducePosition(); }}
          placeholder="sell price $"
          className="bg-[#0a0e14] border border-[#334155] focus:border-amber-500/60 focus:outline-none rounded px-2 py-0.5 text-[11px] tabular-nums text-amber-200 w-[88px]"
          title="Sell price — informational for now (realized P&L logging coming). Cost basis is preserved on the remaining shares."
        />
        <button
          type="button"
          onClick={handleReducePosition}
          disabled={saving}
          className="px-2 py-0.5 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/15 hover:border-amber-500/70 uppercase tracking-wide font-semibold text-[10px] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? 'saving…' : 'trim'}
        </button>
        {reduceError && <span className="text-red-400 text-[10px]">{reduceError}</span>}
        <span className="ml-auto text-[#64748b] text-[9px] italic">
          {(() => {
            const sym = reduceSym.trim().toUpperCase();
            const sh = parseFloat(reduceShares);
            const sellPx = parseFloat(reducePrice);
            if (!sym || !Number.isFinite(sh) || sh <= 0) return 'cost basis preserved on remaining shares';
            const entry = portfolio?.entryPrices?.[sym];
            if (!entry || !Number.isFinite(sellPx) || sellPx <= 0) return `selling ${sh} shares`;
            const gain = sh * (sellPx - entry);
            const gainColor = gain >= 0 ? 'text-emerald-400' : 'text-red-400';
            return <><span>realized:</span> <span className={gainColor}>{gain >= 0 ? '+' : ''}${gain.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></>;
          })()}
        </span>
      </div>
    </>
  );
}
